import React, { useState } from 'react';

const IpadDistribution = () => {
  const [activeTab, setActiveTab] = useState('priority');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeachers, setSelectedTeachers] = useState([]);

  // Data contoh untuk menunjukkan alur sistem
  const teachers = [
    {
      id: 1,
      name: 'Guru A',
      subject: 'Mapel 1',
      grade: 'SMA',
      paymentStatus: 'lunas',
      paymentDate: '2024-01-15',
      techSkill: 'advanced',
      priority: 1,
      phone: '081XXXXXXXX',
      email: 'guru.a@alwafi.sch.id'
    },
    {
      id: 2,
      name: 'Guru B',
      subject: 'Mapel 2',
      grade: 'SMP',
      paymentStatus: 'lunas',
      paymentDate: '2024-01-20',
      techSkill: 'intermediate',
      priority: 2,
      phone: '081XXXXXXXX',
      email: 'guru.b@alwafi.sch.id'
    },
    {
      id: 3,
      name: 'Guru C',
      subject: 'Mapel 3',
      grade: 'SMA',
      paymentStatus: 'cicilan',
      paymentDate: '2024-02-01',
      techSkill: 'beginner',
      priority: 3,
      phone: '081XXXXXXXX',
      email: 'guru.c@alwafi.sch.id'
    }
  ];

  const distributionSchedule = [
    {
      phase: 'Fase 1',
      date: 'Minggu ke-2 September 2025',
      quantity: 'X unit',
      criteria: 'Guru yang sudah lunas + Tech skill tinggi',
      status: 'upcoming'
    },
    {
      phase: 'Fase 2',
      date: 'Minggu ke-4 September 2025',
      quantity: 'Y unit',
      criteria: 'Guru yang sudah lunas + Tech skill menengah',
      status: 'upcoming'
    },
    {
      phase: 'Fase 3',
      date: 'Minggu ke-2 Oktober 2025',
      quantity: 'Z unit',
      criteria: 'Guru cicilan yang hampir lunas',
      status: 'planned'
    }
  ];

  const documentation = [
    {
      title: 'Form Serah Terima iPad',
      description: 'Form untuk mencatat penyerahan iPad kepada guru',
      status: 'required',
      responsible: 'PIC Div Media'
    },
    {
      title: 'Dokumentasi Foto/Video',
      description: 'Dokumentasi proses pembagian iPad untuk arsip dan publikasi',
      status: 'required',
      responsible: 'PIC Div Media'
    },
    {
      title: 'Data Inventory iPad',
      description: 'Pencatatan serial number, kondisi, dan accessories',
      status: 'required',
      responsible: 'Tim IT'
    },
    {
      title: 'Agreement Digital',
      description: 'Perjanjian penggunaan dan tanggung jawab iPad',
      status: 'required',
      responsible: 'PIC Div Media'
    },
    {
      title: 'Training Schedule',
      description: 'Jadwal pelatihan penggunaan iPad untuk guru',
      status: 'recommended',
      responsible: 'Tim Training'
    }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      lunas: 'bg-green-100 text-green-800 border border-green-200',
      cicilan: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      belum: 'bg-red-100 text-red-800 border border-red-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getTechSkillBadge = (skill) => {
    const styles = {
      expert: 'bg-purple-100 text-purple-800 border border-purple-200',
      advanced: 'bg-blue-100 text-blue-800 border border-blue-200',
      intermediate: 'bg-green-100 text-green-800 border border-green-200',
      beginner: 'bg-orange-100 text-orange-800 border border-orange-200'
    };
    return styles[skill] || 'bg-gray-100 text-gray-800';
  };

  const filteredTeachers = teachers.filter(teacher =>
    teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const priorityTeachers = filteredTeachers
    .filter(teacher => teacher.paymentStatus === 'lunas')
    .sort((a, b) => a.priority - b.priority);

  const toggleTeacherSelection = (teacherId) => {
    setSelectedTeachers(prev =>
      prev.includes(teacherId)
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6">
            <i className="fas fa-tablet-alt text-white text-2xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Alur Distribusi iPad untuk Guru
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Sistem manajemen alur pengambilan iPad dengan prioritas berdasarkan status pembayaran
          </p>
        </div>

        {/* Alert Info */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fas fa-info-circle text-blue-400 text-xl"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-blue-800">
                Alur Pengambilan iPad
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Prioritas utama: Guru yang sudah melunasi pembayaran</li>
                  <li>Total iPad yang akan didistribusi: Sesuai ketersediaan</li>
                  <li>Periode distribusi: September - Oktober 2025</li>
                  <li>PIC Dokumentasi: Divisi Media</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'priority', name: 'Daftar Prioritas', icon: 'fas fa-star' },
                { id: 'schedule', name: 'Alur Pengambilan', icon: 'fas fa-calendar' },
                { id: 'documentation', name: 'Dokumentasi', icon: 'fas fa-file-alt' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Priority Tab */}
            {activeTab === 'priority' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">
                    Daftar Prioritas Guru
                  </h2>
                  <div className="w-full sm:w-auto">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Cari guru atau mata pelajaran..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <i className="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                    </div>
                  </div>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100">Sudah Lunas</p>
                        <p className="text-3xl font-bold">{teachers.filter(t => t.paymentStatus === 'lunas').length}</p>
                      </div>
                      <i className="fas fa-check-circle text-3xl text-green-200"></i>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-100">Cicilan</p>
                        <p className="text-3xl font-bold">{teachers.filter(t => t.paymentStatus === 'cicilan').length}</p>
                      </div>
                      <i className="fas fa-clock text-3xl text-yellow-200"></i>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100">Prioritas Tinggi</p>
                        <p className="text-3xl font-bold">{priorityTeachers.filter(t => t.priority <= 2).length}</p>
                      </div>
                      <i className="fas fa-star text-3xl text-blue-200"></i>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100">Terpilih</p>
                        <p className="text-3xl font-bold">{selectedTeachers.length}</p>
                      </div>
                      <i className="fas fa-users text-3xl text-purple-200"></i>
                    </div>
                  </div>
                </div>

                {/* Teachers Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTeachers(priorityTeachers.map(t => t.id));
                                } else {
                                  setSelectedTeachers([]);
                                }
                              }}
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Guru
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status Bayar
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tech Skill
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prioritas
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Kontak
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {priorityTeachers.map((teacher) => (
                          <tr key={teacher.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedTeachers.includes(teacher.id)}
                                onChange={() => toggleTeacherSelection(teacher.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                  <span className="text-white font-medium text-sm">
                                    {teacher.name.split(' ')[0].charAt(0)}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {teacher.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {teacher.subject} - {teacher.grade}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(teacher.paymentStatus)}`}>
                                {teacher.paymentStatus === 'lunas' ? 'Lunas' : 'Cicilan'}
                              </span>
                              <div className="text-xs text-gray-500 mt-1">
                                {teacher.paymentDate}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTechSkillBadge(teacher.techSkill)}`}>
                                {teacher.techSkill}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <i
                                    key={i}
                                    className={`fas fa-star text-sm ${
                                      i < (6 - teacher.priority) ? 'text-yellow-400' : 'text-gray-300'
                                    }`}
                                  ></i>
                                ))}
                                <span className="ml-2 text-sm text-gray-600">
                                  #{teacher.priority}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div>{teacher.phone}</div>
                              <div className="text-xs">{teacher.email}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {selectedTeachers.length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-800 font-medium">
                        {selectedTeachers.length} guru dipilih untuk distribusi
                      </span>
                      <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200">
                        Proses Distribusi
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Schedule Tab */}
            {activeTab === 'schedule' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Alur Pengambilan iPad
                </h2>
                <div className="space-y-6">
                  {distributionSchedule.map((phase, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            phase.status === 'upcoming' ? 'bg-blue-100 text-blue-600' :
                            phase.status === 'planned' ? 'bg-yellow-100 text-yellow-600' :
                            'bg-green-100 text-green-600'
                          }`}>
                            <i className="fas fa-calendar-alt"></i>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {phase.phase}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">
                              {phase.date}
                            </p>
                            <p className="text-sm text-gray-700 mb-2">
                              <span className="font-medium">Jumlah:</span> {phase.quantity}
                            </p>
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Kriteria:</span> {phase.criteria}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          phase.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                          phase.status === 'planned' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {phase.status === 'upcoming' ? 'Segera' :
                           phase.status === 'planned' ? 'Direncanakan' : 'Selesai'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documentation Tab */}
            {activeTab === 'documentation' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Dokumentasi & Requirements
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {documentation.map((doc, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            doc.status === 'required' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                          }`}>
                            <i className="fas fa-file-alt"></i>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {doc.title}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {doc.description}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          doc.status === 'required' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {doc.status === 'required' ? 'Wajib' : 'Opsional'}
                        </span>
                      </div>
                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">PIC:</span> {doc.responsible}
                          </div>
                          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                            Download Template
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Items */}
                <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-4">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Action Items untuk PIC Div Media
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <input type="checkbox" className="mt-1 rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500" />
                      <span className="text-yellow-800">Siapkan form serah terima iPad beserta template tanda tangan digital</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <input type="checkbox" className="mt-1 rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500" />
                      <span className="text-yellow-800">Koordinasi dengan tim IT untuk pencatatan inventory dan serial number</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <input type="checkbox" className="mt-1 rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500" />
                      <span className="text-yellow-800">Siapkan kamera/videographer untuk dokumentasi proses pembagian</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <input type="checkbox" className="mt-1 rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500" />
                      <span className="text-yellow-800">Buat agreement digital tentang tanggung jawab penggunaan iPad</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <input type="checkbox" className="mt-1 rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500" />
                      <span className="text-yellow-800">Koordinasi jadwal training penggunaan iPad dengan tim pelatihan</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IpadDistribution;
